import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'

import { ConsultationStatus, DesmosActStatus } from '@/core/models/consultation.model'
import { useEndConsultationMutation, useRetryDesmosActsMutation } from '@/core/services/consultation'
import DesmosActsDetailDialog from '@/pages/network/components/DesmosActsDetailDialog'

type DesmosPaymentSectionProps = {
  consultationId: string
  consultationStatus: ConsultationStatus
  desmosActStatus?: DesmosActStatus
  waitingRoomId?: string
  networkId?: string
}

function DesmosStatusChip({ desmosActStatus }: { desmosActStatus?: DesmosActStatus }) {
  const { t } = useTranslation()

  if (!desmosActStatus) {
    return (
      <Chip
        icon={<HourglassEmptyIcon />}
        label={t('page.liveConsultation.payment.desmos.status.notProcessed', { defaultValue: 'Non traité' })}
        color="default"
        size="small"
        variant="outlined"
      />
    )
  }

  const { success, acts = [] } = desmosActStatus
  const failedActs = acts.filter((a) => !a.success)

  if (success && failedActs.length === 0) {
    return (
      <Chip
        icon={<CheckCircleIcon />}
        label={t('page.liveConsultation.payment.desmos.status.success', { defaultValue: 'Envoyé à Desmos' })}
        color="success"
        size="small"
        variant="filled"
      />
    )
  }

  if (failedActs.length > 0) {
    return (
      <Chip
        icon={<WarningAmberIcon />}
        label={t('page.liveConsultation.payment.desmos.status.partial', { defaultValue: '{{count}} acte(s) en erreur', count: failedActs.length })}
        color="warning"
        size="small"
        variant="filled"
      />
    )
  }

  return (
    <Chip
      icon={<ErrorIcon />}
      label={t('page.liveConsultation.payment.desmos.status.error', { defaultValue: 'Échec' })}
      color="error"
      size="small"
      variant="filled"
    />
  )
}

export function DesmosPaymentSection({ consultationId, consultationStatus, desmosActStatus, waitingRoomId, networkId }: DesmosPaymentSectionProps) {
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const [detailDialogOpen, setDetailDialogOpen] = useState(false)

  const [endConsultation] = useEndConsultationMutation()
  const [retryDesmosActs] = useRetryDesmosActsMutation()
  const [isEnding, setIsEnding] = useState(false)
  const [isRetrying, setIsRetrying] = useState(false)

  const isActive = consultationStatus === ConsultationStatus.Active
  const isCompleted = consultationStatus === ConsultationStatus.Completed
  const hasStatus = !!desmosActStatus
  const allSuccess = hasStatus && desmosActStatus!.success && !desmosActStatus!.acts?.some((a) => !a.success)

  const handleEndConsultation = async () => {
    setIsEnding(true)
    try {
      await endConsultation(consultationId).unwrap()
      enqueueSnackbar(
        t('page.liveConsultation.payment.desmos.endConsultation.success', { defaultValue: 'Consultation completed. Desmos billing is being processed.' }),
        { variant: 'success' }
      )
    } catch {
      enqueueSnackbar(
        t('page.liveConsultation.payment.desmos.endConsultation.error', { defaultValue: 'An error occurred while ending the consultation.' }),
        { variant: 'error' }
      )
    } finally {
      setIsEnding(false)
    }
  }

  const handleRetry = async () => {
    setIsRetrying(true)
    try {
      await retryDesmosActs({ consultationId, networkId: networkId || '', waitingRoomId: waitingRoomId || '' }).unwrap()
      enqueueSnackbar(
        t('page.liveConsultation.payment.desmos.retry.success', { defaultValue: 'Desmos billing restarted.' }),
        { variant: 'success' }
      )
    } catch {
      enqueueSnackbar(
        t('page.liveConsultation.payment.desmos.retry.error', { defaultValue: 'Erreur lors de la relance de la facturation Desmos.' }),
        { variant: 'error' }
      )
    } finally {
      setIsRetrying(false)
    }
  }

  return (
    <>
      <Paper
        sx={{
          borderRadius: '8px',
          boxShadow: '0px 20px 25px -5px rgba(0, 0, 0, 0.1), 0px 10px 10px -5px rgba(0, 0, 0, 0.04)',
          padding: '24px',
          width: '100%',
        }}
      >
        <Stack spacing={2}>
            {/* Header */}
            <Stack direction="row" alignItems="center" spacing={1}>
              <ReceiptLongIcon color="action" fontSize="small" />
              <Typography variant="subtitle1" fontWeight={600}>
                {t('page.liveConsultation.payment.desmos.title', { defaultValue: 'Facturation Desmos' })}
              </Typography>
              <Box sx={{ flex: 1 }} />
              <DesmosStatusChip desmosActStatus={desmosActStatus} />
            </Stack>

            <Divider />

            {/* Active consultation — doctor may have disconnected */}
            {isActive && (
              <Alert severity="warning" icon={<WarningAmberIcon />}>
                <Typography variant="body2" fontWeight={500}>
                  {t('page.liveConsultation.payment.desmos.activeAlert.title', { defaultValue: 'Has the doctor completed the consultation?' })}
                </Typography>
                <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                  {t('page.liveConsultation.payment.desmos.activeAlert.description', {
                    defaultValue:
                      'If the doctor disconnected without clicking "End", click below to close the consultation and automatically start Desmos billing.',
                  })}
                </Typography>
              </Alert>
            )}

            {/* Completed + Desmos in progress (status not yet available) */}
            {isCompleted && !hasStatus && (
              <Stack direction="row" alignItems="center" spacing={1}>
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">
                  {t('page.liveConsultation.payment.desmos.processing', { defaultValue: 'Traitement Desmos en cours…' })}
                </Typography>
              </Stack>
            )}

            {/* Completed + success */}
            {isCompleted && allSuccess && (
              <Alert severity="success">
                <Typography variant="body2">
                  {t('page.liveConsultation.payment.desmos.successMessage', {
                    defaultValue: 'Les actes ont été envoyés à Desmos avec succès. Vous pouvez accéder au logiciel Desmos pour finaliser la facturation.',
                  })}
                </Typography>
              </Alert>
            )}

            {/* Completed + failed or partial */}
            {isCompleted && hasStatus && !allSuccess && (
              <Alert severity="error">
                <Typography variant="body2">
                  {t('page.liveConsultation.payment.desmos.errorMessage', {
                    defaultValue: 'Certains actes n\'ont pas pu être envoyés à Desmos. Consultez le détail pour les créer manuellement.',
                  })}
                </Typography>
              </Alert>
            )}

            {/* Action buttons */}
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {/* End consultation (only when still active) */}
              {isActive && (
                <Button
                  variant="contained"
                  color="warning"
                  size="small"
                  onClick={handleEndConsultation}
                  disabled={isEnding}
                  startIcon={isEnding ? <CircularProgress size={14} color="inherit" /> : undefined}
                >
                  {t('page.liveConsultation.payment.desmos.endAndBillButton', { defaultValue: 'Terminer et facturer' })}
                </Button>
              )}

              {/* Retry (only for completed + failed) */}
              {isCompleted && hasStatus && !allSuccess && (
                <Button
                  variant="outlined"
                  color="warning"
                  size="small"
                  onClick={handleRetry}
                  disabled={isRetrying}
                  startIcon={isRetrying ? <CircularProgress size={14} color="inherit" /> : undefined}
                >
                  {t('page.liveConsultation.payment.desmos.retryButton', { defaultValue: 'Relancer' })}
                </Button>
              )}

              {/* View details (when status exists) */}
              {isCompleted && hasStatus && (
                <Button variant="text" size="small" onClick={() => setDetailDialogOpen(true)}>
                  {t('page.liveConsultation.payment.desmos.detailButton', { defaultValue: 'View details' })}
                </Button>
              )}
            </Stack>
        </Stack>
      </Paper>

      {isCompleted && hasStatus && (
        <DesmosActsDetailDialog
          open={detailDialogOpen}
          onClose={() => setDetailDialogOpen(false)}
          consultationId={consultationId}
          desmosActStatus={desmosActStatus}
          networkId={networkId || ''}
          waitingRoomId={waitingRoomId || ''}
        />
      )}
    </>
  )
}

export default DesmosPaymentSection
